import { useMemo, Fragment } from 'react';
import { Badge } from '@/components/ui/badge';
import { parseRef, rangesOverlap } from '@/lib/utils/reference-utils';
import { isValidSimpleRef, findPassageIdForRef } from './literary-context-utils';
import { normalizeBookName } from '@/lib/utils/bible-utils';

/**
 * Formats a reference as a clickable link
 * @param book - The book name
 * @param ref - The reference string
 * @param isDarkMode - Whether dark mode is enabled
 * @param setUrl - Function to navigate to a URL
 * @param linkToContext - Optional context type to link to ('culture', 'literary', or 'book')
 * @param allPassages - Optional array of all passages (required if linkToContext is specified)
 * @returns JSX element with clickable link or plain text
 */
export function formatRefAsLink(
    book: string,
    ref: string,
    isDarkMode: boolean,
    setUrl: (url: string) => void,
    linkToContext?: 'culture' | 'literary' | 'book',
    allPassages?: Passage[]
) {
    // If the reference is not in a simple format, display as plain text
    if (!isValidSimpleRef(ref)) {
        return <span className="text-muted-foreground">{ref}</span>;
    }

    // Parse ref like "8:14" or "8:14-16" or "8-9"
    const parts = ref.split(':');
    let chapter: string;
    let verse: string | undefined;

    if (parts.length === 2) {
        // Format: "8:14" or "8:14-16"
        chapter = parts[0];
        verse = parts[1].split('-')[0]; // Take first verse if it's a range
    } else {
        // Format: "8" or "8-9"
        chapter = ref.split('-')[0];
    }

    let url: string;

    // If linkToContext is specified and we have passages, try to find the passage ID
    if (linkToContext && allPassages) {
        const passageId = findPassageIdForRef(book, ref, allPassages);
        if (passageId) {
            url = `/app/passage/${passageId}/context/${linkToContext}`;
        } else {
            // Fallback to read page if passage not found
            url = verse
                ? `/app/read/${book}/${chapter}?verse=${verse}`
                : `/app/read/${book}/${chapter}`;
        }
    } else {
        // Default to read page
        url = verse
            ? `/app/read/${book}/${chapter}?verse=${verse}`
            : `/app/read/${book}/${chapter}`;
    }

    const displayText = `${ref}`;

    return (
        <span
            onClick={() => setUrl(url)}
            className={`cursor-pointer hover:underline ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}
        >
            {displayText}
        </span>
    );
}

interface ReferenceLinksProps {
    refs: string[];
    book: string;
    isDarkMode: boolean;
    setUrl: (url: string) => void;
    linkToContext?: 'culture' | 'literary' | 'book';
    allPassages?: Passage[];
}

/**
 * Component for rendering reference links
 */
export function ReferenceLinks({ refs, book, isDarkMode, setUrl, linkToContext, allPassages }: ReferenceLinksProps) {
    return (
        <>
            {refs.map((ref: string, refIndex: number) => (
                <span key={`ref-${book}-${ref}-${refIndex}`}>
                    {refIndex > 0 && ', '}
                    {formatRefAsLink(book, ref, isDarkMode, setUrl, linkToContext, allPassages)}
                </span>
            ))}
        </>
    );
}

interface PatternItemProps {
    pattern: LiteraryFeaturesByBook['repeatedPatterns'][0];
    book: string;
    isDarkMode: boolean;
    setUrl: (url: string) => void;
}

/**
 * Component for rendering a single repeated pattern
 */
export function PatternItem({ pattern, book, isDarkMode, setUrl }: PatternItemProps) {
    const allRefs = useMemo(() =>
        [...(pattern.refs || []), ...(pattern.ranges || [])],
        [pattern.refs, pattern.ranges]
    );

    return (
        <div className="text-sm">
            <div className="flex items-center justify-between gap-2 mb-2">
                <h5 className="font-medium">{pattern.label}</h5>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">
                        {pattern.count} times
                    </span>
                </div>
            </div>

            <div className="text-xs text-muted-foreground">
                <Badge variant="outline" className="text-xs">
                    {pattern.kind}
                </Badge>
                {allRefs.length > 0 && <>
                    <span className="font-medium ml-2">
                        {allRefs.length === pattern.count ? '' : 'Examples: '}
                    </span>
                    <ReferenceLinks refs={allRefs} book={book} isDarkMode={isDarkMode} setUrl={setUrl} />
                </>}
            </div>
        </div>
    );
}

interface ThemeTrajectoryItemProps {
    themeTrajectory: LiteraryFeaturesByBook['themeTrajectory'][0];
    book: string;
    passage?: Passage;
    isDarkMode: boolean;
    setUrl: (url: string) => void;
    allPassages: Passage[];
}

/**
 * Component for rendering a theme trajectory with optional passage highlighting
 */
export function ThemeTrajectoryItem({ themeTrajectory, book, passage, isDarkMode, setUrl, allPassages }: ThemeTrajectoryItemProps) {
    // Determine which steps are in the current passage
    const stepsWithHighlight = useMemo(() => {
        if (!passage) {
            return (themeTrajectory.trajectory || []).map(step => ({ ...step, inPassage: false }));
        }

        const passageRange = parseRef(passage.ref);
        if (!passageRange) {
            return (themeTrajectory.trajectory || []).map(step => ({ ...step, inPassage: false }));
        }

        return (themeTrajectory.trajectory || []).map(step => {
            const inPassage = (step.refs || []).some(ref => {
                const refRange = parseRef(ref);
                return refRange && rangesOverlap(passageRange, refRange);
            });
            return { ...step, inPassage };
        });
    }, [themeTrajectory.trajectory, passage]);

    return (
        <div>
            <h4 className="font-medium text-sm mb-2">{themeTrajectory.theme} in {normalizeBookName(passage?.book || book)}</h4>
            <div className="border border-muted rounded-md overflow-hidden">
                <div className="grid grid-cols-[auto_1fr]">
                    {stepsWithHighlight.map((step, stepIndex: number) => (
                        <Fragment key={`theme-${themeTrajectory.theme}-step-${stepIndex}`}>
                            <div
                                className={`text-xs text-muted-foreground whitespace-nowrap relative px-3 py-2 ${stepIndex > 0 ? 'border-t border-muted' : ''} ${step.inPassage ? 'bg-accent/20' : ''}`}
                            >
                                {step.inPassage && (
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                                )}
                                {step.refs?.length > 0 && (
                                    <ReferenceLinks
                                        refs={step.refs}
                                        book={book}
                                        isDarkMode={isDarkMode}
                                        setUrl={setUrl}
                                        linkToContext="book"
                                        allPassages={allPassages}
                                    />
                                )}
                            </div>
                            <div
                                className={`text-sm text-foreground px-3 py-2 ${stepIndex > 0 ? 'border-t border-muted' : ''} ${step.inPassage ? 'bg-accent/20' : ''}`}
                            >
                                {step.step}
                            </div>
                        </Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
}

interface PatternListProps {
    patterns: LiteraryFeaturesByBook['repeatedPatterns'];
    book: string;
    passage?: Passage;
    isDarkMode: boolean;
    setUrl: (url: string) => void;
    filterByPassage?: boolean;
}

/**
 * List component for repeated patterns with optional passage filtering
 */
export function PatternList({
    patterns,
    book,
    passage,
    isDarkMode,
    setUrl,
    filterByPassage = false
}: PatternListProps) {
    const filteredPatterns = useMemo(() => {
        if (!filterByPassage || !passage || !patterns) {
            return patterns || [];
        }

        const passageRange = parseRef(passage.ref);
        if (!passageRange) return patterns;

        return patterns.filter(pattern => {
            const refs = pattern.refs || [];
            const ranges = pattern.ranges || [];
            return refs.some(ref => {
                const refRange = parseRef(ref);
                return refRange && rangesOverlap(passageRange, refRange);
            }) || ranges.some(range => {
                const rangeData = parseRef(range);
                return rangeData && rangesOverlap(passageRange, rangeData);
            });
        });
    }, [patterns, filterByPassage, passage]);

    const keyPrefix = filterByPassage ? 'passage' : 'book';

    if (!filteredPatterns.length) {
        return null;
    }

    return (
        <div className="space-y-3">
            {filteredPatterns.map((pattern, index) => (
                <PatternItem
                    key={`${keyPrefix}-pattern-${index}`}
                    pattern={pattern}
                    book={book}
                    isDarkMode={isDarkMode}
                    setUrl={setUrl}
                />
            ))}
        </div>
    );
}

interface ThemeTrajectoryListProps {
    themeTrajectories: LiteraryFeaturesByBook['themeTrajectory'];
    book: string;
    passage?: Passage;
    isDarkMode: boolean;
    setUrl: (url: string) => void;
    allPassages: Passage[];
}

/**
 * List component for theme trajectories with passage highlighting
 */
export function ThemeTrajectoryList({
    themeTrajectories,
    book,
    passage,
    isDarkMode,
    setUrl,
    allPassages
}: ThemeTrajectoryListProps) {
    if (!themeTrajectories || themeTrajectories.length === 0) {
        return null;
    }

    return (
        <div className="space-y-4">
            {themeTrajectories.map((themeTrajectory, index) => (
                <ThemeTrajectoryItem
                    key={`theme-${index}`}
                    themeTrajectory={themeTrajectory}
                    book={book}
                    passage={passage}
                    isDarkMode={isDarkMode}
                    setUrl={setUrl}
                    allPassages={allPassages}
                />
            ))}
        </div>
    );
}

